import { CircleAlert } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { BrandMark } from '../../components/layout/BrandMark';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { Input } from '../../components/ui/Input';
import { PageSpinner } from '../../components/ui/Spinner';
import { safeNextPath, useAuth } from '../../hooks/useAuth';
import { errorMessage } from '../../lib/api';

export function LoginPage() {
  const { user, isLoading, login, isLoggingIn } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const next = safeNextPath(params.get('next'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Sign in · Hearth CRM';
  }, []);

  if (isLoading) return <PageSpinner />;
  if (user) return <Navigate to={next} replace />;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      await login(email.trim(), password);
      void navigate(next, { replace: true });
    } catch (err) {
      // 401 wrong credentials, 423 locked, 429 rate limited: the server message explains which.
      setError(errorMessage(err));
      setPassword('');
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-canvas px-4 py-12">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(60%_100%_at_50%_0%,var(--color-ember-100),transparent)]"
      />
      <div className="relative w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <BrandMark size={40} />
          <h1 className="mt-4 text-xl font-semibold tracking-tight text-slate-900">Sign in to Hearth</h1>
          <p className="mt-1 text-[13px] text-slate-500">The CRM for pizza-oven and kitchen-equipment sales.</p>
        </div>

        <form
          onSubmit={(e) => void onSubmit(e)}
          className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-surface p-6 shadow-panel"
        >
          {error && (
            <div role="alert" className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700">
              <CircleAlert size={16} aria-hidden className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <Field label="Email">
            <Input
              type="email"
              autoComplete="username"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.ca"
            />
          </Field>
          <Field label="Password">
            <Input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <Button type="submit" variant="primary" loading={isLoggingIn} className="mt-1 h-9 w-full">
            Sign in
          </Button>
        </form>
        <p className="mt-6 text-center text-xs text-slate-400">Forgot your password? Ask your Hearth administrator to reset it.</p>
      </div>
    </div>
  );
}
